import React, { useState } from 'react';
import { 
  TextInput, 
  PasswordInput, 
  Button, 
  Stack, 
  Group,
  Text,
  Divider,
  Select
} from '@mantine/core';
import { IconKey, IconEye, IconEyeOff, IconCloud } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { STSClient, GetCallerIdentityCommand } from '@aws-sdk/client-sts';
import { useAwsCredentials } from '../../../shared/contexts/CredentialsContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

const AWSCredentialsForm = ({ onSubmit, mode }) => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    accessKeyId: '',
    secretAccessKey: '',
    sessionToken: '',
    region: 'us-east-1' // default region
  });
  const { setAwsCredentials } = useAwsCredentials();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.accessKeyId.trim() || !formData.secretAccessKey.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Access Key ID and Secret Access Key are required',
        color: 'red',
      });
      return;
    }

    setLoading(true);

    try {
      await onSubmit(formData);
      setAwsCredentials({ ...formData });

      // Determine destination based on mode prop or query parameter
      const destinationMode = mode || searchParams.get('mode') || 'export';
      const destination = destinationMode === 'import' ? '/import' : '/export';

      notifications.show({
        title: 'Success',
        message: 'AWS credentials validated successfully',
        color: 'green',
      });
      navigate(destination);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to validate AWS credentials',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!formData.accessKeyId.trim() || !formData.secretAccessKey.trim()) {
      notifications.show({
        title: 'Validation Error',
        message: 'Please enter Access Key ID and Secret Access Key to test connection',
        color: 'red',
      });
      return;
    }

    setLoading(true);

    try {
      const stsClient = new STSClient({
        region: formData.region,
        credentials: {
          accessKeyId: formData.accessKeyId,
          secretAccessKey: formData.secretAccessKey,
          sessionToken: formData.sessionToken || undefined,
        },
      });

      const command = new GetCallerIdentityCommand({});
      const response = await stsClient.send(command);

      notifications.show({
        title: 'Test Successful',
        message: `Authenticated as: ${response.Arn}`,
        color: 'green',
      });
    } catch (error) {
      console.error(error);
      notifications.show({
        title: 'Test Failed',
        message: error.message || 'Failed to validate AWS credentials',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack gap="lg">
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            Required credentials for AWS API access
          </Text>
          
          <TextInput
            label="Access Key ID"
            placeholder="AKIA..."
            value={formData.accessKeyId}
            onChange={handleChange('accessKeyId')}
            leftSection={<IconKey size={16} />}
            required
          />

          <PasswordInput
            label="Secret Access Key"
            placeholder="Enter your secret access key"
            value={formData.secretAccessKey}
            onChange={handleChange('secretAccessKey')}
            leftSection={<IconKey size={16} />}
            visibilityToggleIcon={({ reveal }) =>
              reveal ? <IconEyeOff size={18} /> : <IconEye size={18} />
            }
            required
          />

          <Select
            label="AWS Region"
            placeholder="Select AWS region"
            value={formData.region}
            onChange={(value) => setFormData(prev => ({ ...prev, region: value }))}
            data={[
              { value: 'us-east-1', label: 'US East (N. Virginia)' },
              { value: 'us-west-2', label: 'US West (Oregon)' },
              { value: 'af-south-1', label: 'Africa (Cape Town)' },
              { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
              { value: 'ap-northeast-2', label: 'Asia Pacific (Seoul)' },
              { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
              { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
              { value: 'ca-central-1', label: 'Canada (Central)' },
              { value: 'eu-central-1', label: 'Europe (Frankfurt)' },
              { value: 'eu-west-2', label: 'Europe (London)' },              
              { value: 'us-gov-west-1', label: 'AWS GovCloud (US-West)' },
            ]}
          />

          <Divider label="Optional" labelPosition="center" />

          <PasswordInput
            label="Session Token"
            placeholder="Enter session token (for temporary credentials)"
            value={formData.sessionToken}
            onChange={handleChange('sessionToken')}
            leftSection={<IconCloud size={16} />}
            visibilityToggleIcon={({ reveal }) =>
              reveal ? <IconEyeOff size={18} /> : <IconEye size={18} />
            }
          />

          <Text size="xs" c="dimmed">
            Session token is only required when using temporary AWS credentials (STS tokens).
          </Text>
        </Stack>

        <Group justify="space-between">
          <Button
            variant="light"
            onClick={handleTestConnection}
            loading={loading}
            leftSection={<IconCloud size={16} />}
          >
            Test Connection
          </Button>

          <Button
            type="submit"
            loading={loading}
            leftSection={<IconKey size={16} />}
          >
            Save Credentials
          </Button>
        </Group>
      </Stack>
    </form>
  );
};

export default AWSCredentialsForm;